import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Sparkles, ArrowLeft, ArrowRight, ShieldCheck, Briefcase, 
  Scale, FileText, Building2, TrendingUp, Award, Users, 
  Search, Lock, FileCheck, Coins, HelpCircle, CheckSquare,
  Factory, X, ShoppingCart, Landmark, Wrench, Shield
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface BusinessCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const BUSINESS_AGENT_IDS = [
  'monitor-operatyvnykh-bezpekovykh-20260924-e5uu',
  'menedzher-zvitnosti-cbam-20260904-bkpk',
  'ekspert-z-monitorynhu-ryzykiv-ko-20260904-zvzs',
  'audytor-dohovirnykh-tsin-20260904-eacq',
  'audytor-tendernykh-propozytsiy-20260904-4j2l',
  'auktsionnyy-investytsiynyy-anali-20260904-qipc',
  'audytor-khmarnykh-kontraktiv-ta--20260904-m8qc',
  'audytor-litsenziy-saas-ta-royalt-20260904-1qok',
  'audytor-zed-kontraktiv-fosfa-20260904-hfqo',
  'audytor-lizynhovykh-uhod-ta-hraf-20260904-zq73',
  // 🏭 Флагмани Промисловості & Модернізації Підприємства
  'industrial-modernization-architect',
  'industrial-park-residency-architect',
  'tech-spec-tor-architect',
  'tech-transfer-commercializer',
  'admin-workplace-safety-iso45001',
  'business-continuity-planner',
  'csddd-supply-chain-auditor',
  'it-auditor',
  'customs-compliance',
  'meeting-transcriber',

  // ⚖️ Юриспруденція, Договори & Арбітраж
  'contract-redline-diff-auditor',
  'contract-disagreement-protocol-generator',
  'legal-penalty-inflation-calculator',
  'legal-pre-trial-claims-builder',
  'legal-aml-kyc-source-of-funds',
  'legal-arbitration-dispute-counsel',
  'legal-corporate-pl-spzoo-incorporator',
  'sha-corporate-agreement-architect',
  'diia-city-compliance-gig-architect',
  'contract-auditor',
  'commercial-contract-auditor',
  'legal-doc-creator',
  'legislation-tracker',
  'court-compliance',
  'aml-auditor',
  'aml-kyc-auditor',
  'ip-guard',
  'ip-patent-attorney',
  'patent-claims-builder',
  'corporate-secretary-minutes',
  'gdpr-privacy-auditor',
  'eu-counterparty-auditor',
  'eu-omnibus-ecommerce-auditor',
  'lease-realestate-auditor',

  // 🏛️ Тендери, Prozorro & Публічні Закупівлі
  'procurement-ted-prozorro-bidder',
  'tender-analyst',
  'b2b-procurement-auditor',
  'public-procurement-compliance',
  'uo-protocols-generator',
  'dasu-monitoring-defender',
  'expected-value-calculator',
  'amku-complaint-defender',
  'amku-tender-appeals',
  'procurement-contract-amender',
  'prozorro-localization-auditor',

  // 📈 Продажі, Фінанси, Гранти & Оцінка
  'website-cro-marketing-auditor',
  'business-dcf-investment-modeler',
  'cfo-financial-analyzer',
  'sales-product-optimizer',
  'market-intelligence',
  'b2b-sales-strategist',
  'credit-risk-evaluator',
  'crm-sales-auditor',
  'grant-writer',
  'erobota-grant-business-plan-architect',
  'startup-pitch',
  'property-valuator',

  // 👔 Кадри, HR & Мобілізаційне Бронювання
  'hr-compliance',
  'hr-recruiter-screening',
  'employee-onboarding-mentor',
  'hr-order-instructor',
  'hr-crossborder-zus-a1-architect',
  'mobilization-booking-critical-enterprise-auditor'
];

const MODERNIZATION_IDS = [
  'industrial-modernization-architect',
  'industrial-park-residency-architect',
  'tech-spec-tor-architect',
  'tech-transfer-commercializer',
  'admin-workplace-safety-iso45001',
  'business-continuity-planner',
  'csddd-supply-chain-auditor',
  'it-auditor',
  'customs-compliance',
  'meeting-transcriber'
];

const LEGAL_IDS = [
  'contract-redline-diff-auditor',
  'contract-disagreement-protocol-generator',
  'legal-penalty-inflation-calculator',
  'legal-pre-trial-claims-builder',
  'legal-aml-kyc-source-of-funds',
  'legal-arbitration-dispute-counsel',
  'legal-corporate-pl-spzoo-incorporator',
  'sha-corporate-agreement-architect',
  'diia-city-compliance-gig-architect',
  'contract-auditor',
  'commercial-contract-auditor',
  'legal-doc-creator',
  'legislation-tracker',
  'court-compliance',
  'aml-auditor',
  'aml-kyc-auditor',
  'ip-guard',
  'patent-claims-builder',
  'corporate-secretary-minutes',
  'gdpr-privacy-auditor',
  'eu-counterparty-auditor',
  'eu-omnibus-ecommerce-auditor',
  'lease-realestate-auditor'
];

const PROCUREMENT_IDS = [
  'procurement-ted-prozorro-bidder',
  'tender-analyst',
  'b2b-procurement-auditor',
  'public-procurement-compliance',
  'uo-protocols-generator',
  'dasu-monitoring-defender',
  'expected-value-calculator',
  'amku-complaint-defender',
  'amku-tender-appeals',
  'procurement-contract-amender',
  'prozorro-localization-auditor'
];

const MARKET_IDS = [
  'website-cro-marketing-auditor',
  'business-dcf-investment-modeler',
  'cfo-financial-analyzer',
  'sales-product-optimizer',
  'market-intelligence',
  'b2b-sales-strategist',
  'credit-risk-evaluator',
  'crm-sales-auditor',
  'grant-writer',
  'erobota-grant-business-plan-architect',
  'startup-pitch',
  'property-valuator'
];

const HR_IDS = [
  'hr-compliance',
  'hr-recruiter-screening',
  'employee-onboarding-mentor',
  'hr-order-instructor',
  'hr-crossborder-zus-a1-architect',
  'mobilization-booking-critical-enterprise-auditor'
];

// Helper icon renderer
const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Briefcase className={className} />;
  return <IconComponent className={className} />;
};

export default function BusinessCenterHubView({
  onLaunchAgent,
  onBack,
  language, 
  currentUserEmail, 
  currency = 'UAH'
}: BusinessCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const [selectedSubTab, setSelectedSubTab] = useState<'all' | 'modernization' | 'legal' | 'procurement' | 'market' | 'hr'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch business pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  // Build sorted list of all agents according to BUSINESS_AGENT_IDS order
  const allBusinessAgents = useMemo(() => {
    const map = new Map<string, any>();
    agentsConfig.forEach((a: any) => map.set(a.id, a));

    const list: any[] = [];
    BUSINESS_AGENT_IDS.forEach((id) => {
      const found = map.get(id);
      if (found) list.push(found);
    });
    return list;
  }, []);

  // Counts for each category
  const modernizationCount = useMemo(() => allBusinessAgents.filter(a => MODERNIZATION_IDS.includes(a.id)).length, [allBusinessAgents]);
  const legalCount = useMemo(() => allBusinessAgents.filter(a => LEGAL_IDS.includes(a.id)).length, [allBusinessAgents]);
  const procurementCount = useMemo(() => allBusinessAgents.filter(a => PROCUREMENT_IDS.includes(a.id)).length, [allBusinessAgents]);
  const marketCount = useMemo(() => allBusinessAgents.filter(a => MARKET_IDS.includes(a.id)).length, [allBusinessAgents]);
  const hrCount = useMemo(() => allBusinessAgents.filter(a => HR_IDS.includes(a.id)).length, [allBusinessAgents]);

  // Filtered agents
  const filteredAgents = useMemo(() => {
    return allBusinessAgents.filter((agent: any) => {
      // 1. SubTab Filter
      if (selectedSubTab === 'modernization' && !MODERNIZATION_IDS.includes(agent.id)) return false;
      if (selectedSubTab === 'legal' && !LEGAL_IDS.includes(agent.id)) return false;
      if (selectedSubTab === 'procurement' && !PROCUREMENT_IDS.includes(agent.id)) return false;
      if (selectedSubTab === 'market' && !MARKET_IDS.includes(agent.id)) return false;
      if (selectedSubTab === 'hr' && !HR_IDS.includes(agent.id)) return false;

      // 2. Search Query Filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const name = (agent.nameUk || agent.name || '').toLowerCase();
        const nameEn = (agent.nameEn || '').toLowerCase();
        const namePl = (agent.namePl || '').toLowerCase();
        const desc = (agent.descriptionUk || agent.description || '').toLowerCase();
        const descEn = (agent.descriptionEn || '').toLowerCase();
        const descPl = (agent.descriptionPl || '').toLowerCase();
        const id = agent.id.toLowerCase();
        const features = [...(agent.featuresUk || []), ...(agent.featuresEn || []), ...(agent.featuresPl || [])].join(' ').toLowerCase();

        return name.includes(q) || nameEn.includes(q) || namePl.includes(q) ||
               desc.includes(q) || descEn.includes(q) || descPl.includes(q) ||
               id.includes(q) || features.includes(q);
      }

      return true;
    });
  }, [allBusinessAgents, selectedSubTab, searchQuery]);

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-md flex items-center gap-1.5">
            <Briefcase className="w-3.5 h-3.5 text-blue-400" />
            {isPl 
              ? `💼 ${allBusinessAgents.length} Modułów Biznesu & Przemysłu` 
              : isUk 
              ? `💼 ${allBusinessAgents.length} Агентів Бізнесу, Промисловості та Права` 
              : `💼 ${allBusinessAgents.length} Business, Industry & Legal Agents`}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-emerald-400" />
            {isPl ? '100% Ochrona Interesów Biznesu' : isUk ? '100% Захист Інтересів Підприємства' : '100% Enterprise Protection'}
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 border border-blue-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-4xl space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
              {isPl 
                ? 'PRZEMYSŁ • MODERNIZACJA • PRAWO • UMOWY • PRZETARGI • SPRZEDAŻ • HR' 
                : isUk 
                ? 'ПРОМИСЛОВІСТЬ • МОДЕРНІЗАЦІЯ • ЮРИСПРУДЕНЦІЯ • ДОГОВОРИ • ТЕНДЕРИ • ПРОДАЖІ • HR' 
                : 'INDUSTRY • MODERNIZATION • LEGAL • CONTRACTS • TENDERS • SALES • HR'}
            </span>
            <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded flex items-center gap-1">
              <Factory className="w-3 h-3 text-emerald-400" />
              {isPl ? 'Nowość: Modernizacja Zakładów' : isUk ? 'Новинка: Модернізація Заводів' : 'New: Factory Modernization'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-white via-slate-100 to-blue-300 bg-clip-text text-transparent">
            {isPl 
              ? 'CENTRUM BIZNESU, PRZEMYSŁU & PRAWA' 
              : isUk 
              ? 'БІЗНЕС, ПРОМИСЛОВІСТЬ & ЮРИСПРУДЕНЦІЯ' 
              : 'BUSINESS, INDUSTRY & LEGAL COMMAND CENTER'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? `Kompleksowe centrum ${allBusinessAgents.length} modułów dla kadry zarządzającej, dyrektorów fabryk, inżynierów, prawników i HR. Techniczna modernizacja zakładów i audyt OEE, analiza umów (Redlining/Kodeks cywilny), przetargi TED/Prozorro, sprzedaż B2B, granty i mobilizacyjne rezerwacje pracowników.`
              : isUk
              ? `Повнофункціональний командний центр з ${allBusinessAgents.length} модулів для топ-менеджменту, директорів заводів, інженерів, юристів та HR. Технічна модернізація виробничих ліній та знос OEE, юридичний аудит і Redline договорів, тендери Prozorro/TED EU, залучення грантів, B2B-продажі та мобілізаційне бронювання.`
              : `Comprehensive operational suite of ${allBusinessAgents.length} AI engines for executives, factory directors, engineers, legal counsels and HR. Industrial plant modernization & OEE audits, contract redlining, TED EU/Prozorro bids, B2B sales pipelines, grants and workforce mobilization compliance.`}
          </p>
        </div>
      </div>

      {/* Interactive Search & Filter Bar */}
      <div className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={
              isPl 
                ? '🔍 Szukaj agenta (np. modernizacja, fabryka, umowa, przetarg, grant, kadr)...' 
                : isUk 
                ? '🔍 Пошук агента (наприклад: модернізація, завод, договір, претензія, тендер, грант, бронювання)...' 
                : '🔍 Search agents (e.g. modernization, factory, contract, claim, tender, grant, hr)...'
            }
            className="w-full pl-10 pr-10 py-3 bg-slate-900/90 border border-slate-800 focus:border-blue-500 rounded-xl text-xs sm:text-sm text-white placeholder:text-slate-500 focus:outline-none transition-all shadow-inner"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white rounded-md hover:bg-slate-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Sub-Category Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
          <button
            onClick={() => setSelectedSubTab('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              selectedSubTab === 'all'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
            }`}
          >
            <Building2 className="w-3.5 h-3.5" />
            <span>{isPl ? `Wszystkie Moduły (${allBusinessAgents.length})` : isUk ? `Всі Модулі (${allBusinessAgents.length})` : `All Modules (${allBusinessAgents.length})`}</span>
          </button>

          <button
            onClick={() => setSelectedSubTab('modernization')}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              selectedSubTab === 'modernization'
                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800 hover:border-emerald-500/40'
            }`}
          >
            <Factory className="w-3.5 h-3.5 text-emerald-400" />
            <span>{isPl ? `🏭 Przemysł & Modernizacja (${modernizationCount})` : isUk ? `🏭 Промисловість & Модернізація (${modernizationCount})` : `🏭 Industry & Modernization (${modernizationCount})`}</span>
          </button>

          <button
            onClick={() => setSelectedSubTab('legal')}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              selectedSubTab === 'legal'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
            }`}
          >
            <Scale className="w-3.5 h-3.5 text-blue-400" />
            <span>{isPl ? `⚖️ Prawo & Umowy (${legalCount})` : isUk ? `⚖️ Юриспруденція & Договори (${legalCount})` : `⚖️ Legal & Contracts (${legalCount})`}</span>
          </button>

          <button
            onClick={() => setSelectedSubTab('procurement')}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              selectedSubTab === 'procurement'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
            }`}
          >
            <Landmark className="w-3.5 h-3.5 text-amber-400" />
            <span>{isPl ? `🏛️ Przetargi & Prozorro (${procurementCount})` : isUk ? `🏛️ Тендери & Prozorro (${procurementCount})` : `🏛️ Tenders & Bids (${procurementCount})`}</span>
          </button>

          <button
            onClick={() => setSelectedSubTab('market')}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              selectedSubTab === 'market'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5 text-purple-400" />
            <span>{isPl ? `📈 Sprzedaż & Dotacje (${marketCount})` : isUk ? `📈 Продажі & Гранти (${marketCount})` : `📈 Sales & Grants (${marketCount})`}</span>
          </button>

          <button
            onClick={() => setSelectedSubTab('hr')}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              selectedSubTab === 'hr'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
            }`}
          >
            <Users className="w-3.5 h-3.5 text-cyan-400" />
            <span>{isPl ? `👔 Kadry & Rezerwacja (${hrCount})` : isUk ? `👔 Кадри & Бронювання (${hrCount})` : `👔 HR & Mobilization (${hrCount})`}</span>
          </button>
        </div>
      </div>

      {/* Grid of Agent Cards */}
      {filteredAgents.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center space-y-4">
          <Search className="w-12 h-12 text-slate-600 mx-auto" />
          <h3 className="text-base font-bold text-white uppercase">
            {isPl ? 'Nie znaleziono agentów' : isUk ? 'Агентів не знайдено' : 'No Agents Found'}
          </h3>
          <p className="text-xs text-slate-400 max-w-md mx-auto">
            {isPl 
              ? `Brak wyników dla zapytania "${searchQuery}". Spróbuj wpisać inną frazę lub zresetować filtry.` 
              : isUk 
              ? `За запитом "${searchQuery}" у цій категорії нічого не знайдено. Спробуйте змінити ключові слова або скинути фільтр.` 
              : `No agents match "${searchQuery}". Try different keywords or reset the search filter.`}
          </p>
          <button
            onClick={() => { setSearchQuery(''); setSelectedSubTab('all'); }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold uppercase rounded-xl transition-colors cursor-pointer"
          >
            {isPl ? 'Pokaż wszystkich agentów' : isUk ? 'Показати всіх 61 агента' : 'Show All 61 Agents'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredAgents.map((agent: any) => {
            const agentPricing = pricing[agent.id];
            const priceVal = agentPricing ? agentPricing.price : 490;
            const limitVal = agentPricing ? agentPricing.limit : 30;
            const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;
            const singlePriceVal = agentPricing ? agentPricing.singlePrice : 25;
            const isModernization = agent.id === 'industrial-modernization-architect';

            return (
              <div
                key={agent.id}
                className={`group relative bg-white border rounded-xl p-6 flex flex-col justify-between transition-all duration-300 hover:shadow-xl ${
                  isModernization 
                    ? 'border-emerald-500/60 shadow-md shadow-emerald-500/10 ring-1 ring-emerald-400/30' 
                    : 'border-slate-200 hover:border-blue-500/40 hover:shadow-blue-500/5'
                }`}
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg border group-hover:scale-105 transition-transform ${
                      isModernization 
                        ? 'bg-emerald-50 text-emerald-600 border-emerald-300' 
                        : 'bg-blue-50 text-blue-600 border-blue-200/60'
                    }`}>
                      <AgentIcon name={agent.icon} className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[9px] font-extrabold uppercase px-2 py-0.5 rounded border ${
                          isModernization
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                          {getLocalizedAgentCategory(agent, language)}
                        </span>
                        <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                          ✨ Gemini 3.7 Flash
                        </span>
                        <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200 font-mono">
                          {formatPriceWithCurrency(priceVal, currency)} / {limitVal} {isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : 'req.'} ({durationDaysVal} {isUk ? 'дн.' : isPl ? 'dni' : 'days'})
                        </span>
                        {singlePriceVal > 0 && (
                          <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                            {isUk ? `Разово: ${formatPriceWithCurrency(singlePriceVal, currency)}` : isPl ? `Pojedynczo: ${formatPriceWithCurrency(singlePriceVal, currency)}` : `Single: ${formatPriceWithCurrency(singlePriceVal, currency)}`}
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mt-1.5 leading-snug">
                        {getLocalizedAgentName(agent, language)}
                      </h3>
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed font-mono">
                    {getLocalizedAgentDesc(agent, language)}
                  </p>

                  {/* Features list */}
                  <ul className="space-y-1.5 pt-1">
                    {getLocalizedAgentFeatures(agent, language).map((feat: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2 text-[11px] text-slate-700">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isModernization ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                        <span className="truncate">{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Launch Action */}
                <div className="pt-6 mt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200">
                    <Sparkles className="w-3 h-3 text-emerald-600 animate-pulse" />
                    <span>{isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Run FREE'}</span>
                  </div>

                  <button
                    onClick={() => onLaunchAgent(agent.target)}
                    className={`px-5 py-2.5 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer hover:scale-[1.02] ${
                      isModernization
                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-emerald-600/20'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-blue-600/20'
                    }`}
                  >
                    <span>{isPl ? '🚀 Uruchom Zadanie' : isUk ? '🚀 Виконати Задачу' : '🚀 Execute Task'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


